import type { DataNode } from 'components/WorkspaceEditor/types/types'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import { getSwitcherIconSelector } from './contentAnalysis.utils'

// ====================================================================
// SWITCHER ICON UTILITIES
// ====================================================================

/**
 * Configuration for switcher icon focus operations
 */
const SWITCHER_FOCUS_CONFIG = {
    REFOCUS_DELAY: 100, // Delay to allow DOM updates after expand/collapse
} as const

/**
 * Refocuses the switcher icon after an expand/collapse operation
 *
 * @param nodeKey - The key of the node whose switcher icon should be focused
 * @param fallbackTarget - Optional fallback element to focus if switcher not found
 * @param delay - Optional delay before attempting focus (default: 100ms)
 */
export const refocusSwitcherAfterOperation = (
    nodeKey: string,
    fallbackTarget?: HTMLElement,
    delay: number = SWITCHER_FOCUS_CONFIG.REFOCUS_DELAY
): void => {
    setTimeout(() => {
        const switcherIcon = document.querySelector(
            getSwitcherIconSelector(nodeKey)
        ) as HTMLElement

        if (switcherIcon) {
            switcherIcon.focus()
        } else if (fallbackTarget) {
            fallbackTarget.focus()
        }
    }, delay)
}

/**
 * Handles expand/collapse keyboard events (Enter/Space keys)
 *
 * @param event - The keyboard event
 * @param node - The data node to expand/collapse
 * @param isExpanded - Current expansion state
 * @param expandedKeys - Array of currently expanded keys
 * @param handleExpandCollapseArticle - Function to handle the expand/collapse operation
 * @returns true if the event was handled, false otherwise
 */
export const handleExpandCollapseKeys = (
    event: React.KeyboardEvent<HTMLElement>,
    node: DataNode,
    isExpanded: boolean,
    expandedKeys: string[],
    handleExpandCollapseArticle: (
        keys: string[],
        shouldExpand: boolean,
        node: DataNode
    ) => void
): boolean => {
    const enterKey = tryGetShortcutKey('Enter', event, true)
    const spaceKey = tryGetShortcutKey('Space', event, true)

    // Only handle Enter/Space keys
    if (!enterKey && !spaceKey) {
        return false
    }

    event.preventDefault()
    event.stopPropagation()

    // Store current target for re-focusing
    const currentTarget = event.currentTarget as HTMLElement

    // Perform expand/collapse operation
    if (isExpanded) {
        handleExpandCollapseArticle(expandedKeys, false, node)
    } else {
        handleExpandCollapseArticle([...expandedKeys, node.key], true, node)
    }

    // Re-focus the switcher icon after operation
    refocusSwitcherAfterOperation(node.key, currentTarget)

    return true
}

/**
 * Creates a click handler for switcher icon spans
 *
 * @param shouldExpand - Whether this click should expand (true) or collapse (false)
 * @param node - The data node to expand/collapse
 * @param expandedKeys - Array of currently expanded keys
 * @param handleExpandCollapseArticle - Function to handle the expand/collapse operation
 * @returns Click event handler function
 */
export const createSwitcherClickHandler = (
    shouldExpand: boolean,
    node: DataNode,
    expandedKeys: string[],
    handleExpandCollapseArticle: (
        keys: string[],
        shouldExpand: boolean,
        node: DataNode
    ) => void
) => {
    return (e: React.MouseEvent<HTMLElement>) => {
        const currentTarget = e.currentTarget as HTMLElement

        // Determine keys based on expand/collapse action
        const keys = shouldExpand ? [...expandedKeys, node.key] : expandedKeys

        // Perform expand/collapse operation
        handleExpandCollapseArticle(keys, shouldExpand, node)

        // Re-focus after click to preserve node navigation
        refocusSwitcherAfterOperation(node.key, currentTarget)
    }
}
